import {onMounted, ref} from 'vue';
import {deleteToken, getToken} from 'firebase/messaging';

export function useFirebaseNotification() {
    const { $api } = useNuxtApp();
    const runtimeConfig = useRuntimeConfig();
    const notificationsStore = useNotificationsStore();
    const {
        disabledPushNotificationsWarningClosed,
        unsupportedPushNotificationsWarningClosed,
        notifyOtherDevices,
        pauseNotificationsPageLogic,
        newTokenRequested,
    } = storeToRefs(notificationsStore);
    const notificationPermission = ref<string>(window?.Notification?.permission || '');
    const isNotificationSupported = ref<boolean>(false);

    const setToken = async (updateNotificationState: boolean = true) => {
        const { $messaging } = useNuxtApp();
        if(!$messaging) return;
        notificationsStore.fcmTokenResolving = true;
        try {
            const firebaseToken = await getToken($messaging, {
                vapidKey: runtimeConfig.public.firebaseVapIdKey as string,
            });
            await $api.v2.storeUserFirebaseCloudMessagingToken({
                fcm_token: firebaseToken,
                device_info: navigator.userAgent,
                notify_other_devices: !!notifyOtherDevices.value
            });
            if(updateNotificationState) {
                await storeUserNotificationStatus(true);
            }
            notificationsStore.fcmToken = firebaseToken;
        } catch(error) {
            console.error('Failed to setToken:', error);
        } finally {
            notificationsStore.fcmTokenResolving = false;
        }
    };

    const basicSetToken = async () => {
        pauseNotificationsPageLogic.value = true;
        const { $messaging } = useNuxtApp();
        if(!$messaging) {
            pauseNotificationsPageLogic.value = false;
            return;
        }
        try {
            notificationsStore.fcmToken = await getToken($messaging, {
                vapidKey: runtimeConfig.public.firebaseVapIdKey as string,
            });
        } catch(error) {
            console.error('Failed to setToken:', error);
        } finally {
            pauseNotificationsPageLogic.value = false;
        }
    }

    const enableBrowserNotifications = async () => {
        notificationsStore.clearFcmToken();
        if(!('Notification' in window)) {
            unsupportedPushNotificationsWarningClosed.value = false;
            return;
        }

        if(Notification.permission === 'granted') {
            notificationPermission.value = 'granted';
            await setToken();
            return;
        }
        if(Notification.permission === 'denied') {
            notificationPermission.value = 'denied';
            return;
        }

        // permission === 'default'  -> show the prompt
        try {
            const permission = await Notification.requestPermission();
            if(permission === 'granted') {
                notificationPermission.value = 'granted';
                // setToken() calls storeUserNotificationStatus(true) internally — no need to call it here.
                await setToken();
            } else {
                notificationPermission.value = permission;
                disabledPushNotificationsWarningClosed.value = false;
            }
        } catch (err) {
            console.error('Notification request failed', err);
        }
    }

    const optOutFromAll = async (optOutApiCall: boolean = true) => {
        pauseNotificationsPageLogic.value = true;
        const { $messaging } = useNuxtApp();
        if(!$messaging) {
            pauseNotificationsPageLogic.value = false;
            return;
        }
        try {
            await deleteToken($messaging);
            if(optOutApiCall) {
                await $api.v2.unsubscribeFromFirebaseNotifications({
                    notify_other_devices: !!notifyOtherDevices.value
                });
            }
            notificationsStore.clearFcmToken();
            // Reset so the login watcher re-requests a token if the user re-enables
            // notifications or a different user logs in on the same device.
            newTokenRequested.value = false;
        } catch(error) {
            console.error('Failed optOutFromAll:', error);
        } finally {
            pauseNotificationsPageLogic.value = false;
        }
    };

    const storeUserNotificationStatus = async (notificationsEnabled: boolean) => {
        await $api.v2.storeUserNotificationStatus({
            notification_enabled: notificationsEnabled,
        });
    }

    if (import.meta.client) {
        isNotificationSupported.value = 'Notification' in window;
    }

    return {
        setToken,
        basicSetToken,
        enableBrowserNotifications,
        optOutFromAll,
        notificationPermission,
        isNotificationSupported,
    };
}
